import { styled } from '@linaria/react';
import theme from '../../theme';

const { pageHeaderHeight, adminContentPadding } = theme.constants;

export const ChangelogPage = styled.div`
    display: flex;
    flex-direction: column;
    height: calc(100vh - ${pageHeaderHeight} - ${adminContentPadding} - ${adminContentPadding});
    overflow: hidden;

    @media (max-width: 768px) {
        padding: 0.5rem 0;
        height: auto;
        height: calc(100vh - ${pageHeaderHeight} - ${adminContentPadding} - ${adminContentPadding});
    }
`;

export const ChangelogHeader = styled.div`
    text-align: center;
    margin-bottom: 2rem;

    h1 {
        font-size: 2.5rem;
        font-weight: 300;
        color: var(--primary-color);
        margin-bottom: 1rem;
    }

    p {
        font-size: 1.1rem;
        color: var(--text-secondary);
        max-width: 600px;
        margin: 0 auto;
    }

    @media (max-width: 768px) {
        margin-bottom: 1.5rem;

        h1 {
            font-size: 2rem;
        }
    }
`;

export const FilterBar = styled.div`
    margin-bottom: 1rem;
    
    ul {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 1rem;
        list-style: none;
        padding: 0;
        margin: 0;

        li {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        li.spacer {
            flex: 1;
        }

        li.load-more-info {
            font-size: 0.8rem;
            color: #6c757d;
            white-space: nowrap;
        }

        @media (max-width: 768px) {
            flex-wrap: wrap;
            gap: 0.5rem;
        }
    }
`;

export const FilterLabel = styled.label`
    font-size: 0.875rem;
    font-weight: 500;
    color: #495057;
    white-space: nowrap;
`;

export const Pagination = styled.div`
    margin-top: 1rem;

    ul {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.5rem;
        list-style: none;
        padding: 0;
        margin: 0;
    }
`;

export const LoadMoreButton = styled.button`
    padding: 0.4rem 1rem;
    border: 1px solid var(--primary-color);
    background: var(--primary-color);
    color: white;
    cursor: pointer;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 500;
    white-space: nowrap;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: var(--primary-color-dark, #1565c0);
        border-color: var(--primary-color-dark, #1565c0);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &:active:not(:disabled) {
        transform: scale(0.97);
    }
`;

export const YearSelect = styled.div`
    margin-right: 16px;
`;

export const BorderedContainer = styled.div`
    border: 1px solid #e9ecef;
    border-radius: 6px;
    overflow: hidden;
    background: white;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

export const TableScrollContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    background: white;

    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;

        &:hover {
            background: #a1a1a1;
        }
    }
`;

export const Table = styled.table`
    width: 100%;
    margin: 0;
    border-collapse: collapse;
`;

export const ChangeSetList = styled.table`
    width: 100%;
    margin: 0;
    border-collapse: collapse;

    thead {
        background: rgb(238 238 238);
        border-bottom: 1px solid var(--border-color);
        position: sticky;
        top: 0;
        z-index: 1;

        th {
            padding: 0;
        }
    }

    tbody {
        tr {
            border-bottom: 1px solid #f0f0f0;
            transition: background-color 0.2s ease;

            &:hover {
                background: #f8f9fa;
            }

            &:last-child {
                border-bottom: none;
            }

            td {
                padding: 0;
            }
        }
    }
`;

export const HeaderRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr 2fr 3fr 2fr 2fr;
    gap: 0;
    padding: 0;
    align-items: center;
    background: ${theme.colors.background.tableHeader};
    border-bottom: 2px solid #dee2e6;
    position: sticky;
    top: 0;
    z-index: 1;
    padding-right: 0.5rem;


    .changeType,
    .dateTime,
    .category,
    .itemChanged,
    .changedBy,
    .changeStatus {
        font-size: 0.8rem;
        font-weight: 600;
        color: #495057;
        padding: 0.5rem 0.6rem;
        border-right: 1px solid #dee2e6;
        
        &:last-child {
            border-right: none;
        }
    }

    @media (max-width: 1200px) {
        grid-template-columns: 1fr 1.5fr 1.5fr 2fr 1.5fr 1fr;
        gap: 0;
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr 1.5fr 1.5fr 2fr 1.5fr 1fr;
        min-width: 700px;

        .changeType,
        .dateTime,
        .category,
        .itemChanged,
        .changedBy,
        .changeStatus {
            font-size: 0.75rem;
            padding: 0.5rem 0.6rem;
        }
    }
`;

export const ChangeRow = styled.div<{ clickable?: boolean }>`
    display: grid;
    grid-template-columns: 1fr 2fr 2fr 3fr 2fr 2fr;
    gap: 0;
    padding: 0;
    align-items: center;
    cursor: pointer;
    transition: background-color 0.15s ease;
    border-bottom: 1px solid #e9ecef;

    &:hover {
        background-color: #f8f9fa;
    }

    &:last-child {
        border-bottom: none;
    }

    .changeType,
    .dateTime,
    .category,
    .itemChanged,
    .changedBy,
    .changeStatus {
        font-size: 0.8rem;
        color: #495057;
        padding: 0.5rem 0.6rem;
        border-right: 1px solid #e9ecef;
        display: flex;
        align-items: center;
        
        &:last-child {
            border-right: none;
        }
    }

    @media (max-width: 1200px) {
        grid-template-columns: 1fr 1.5fr 1.5fr 2fr 1.5fr 1fr;
        gap: 0;
    }

    @media (max-width: 768px) {
        grid-template-columns: auto 1fr auto;
        grid-template-rows: auto auto;
        gap: 0 0.5rem;
        padding: 0.5rem 0.6rem;
        align-items: center;

        .changeType {
            grid-row: 1;
            grid-column: 1;
            border-right: none;
            padding: 0;
            font-weight: 600;
            font-size: 0.7rem;
        }

        .itemChanged {
            grid-row: 1;
            grid-column: 2;
            border-right: none;
            padding: 0;
            font-size: 0.75rem;
            font-weight: 600;
            color: #333;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }

        .changeStatus {
            grid-row: 1 / 3;
            grid-column: 3;
            border-right: none;
            padding: 0;
            justify-content: flex-end;
        }

        .dateTime {
            grid-row: 2;
            grid-column: 1;
            border-right: none;
            padding: 0;
            font-size: 0.65rem;
            color: #6c757d;
        }

        .category {
            grid-row: 2;
            grid-column: 2;
            border-right: none;
            padding: 0;
            font-size: 0.65rem;
            color: #6c757d;
        }

        .changedBy {
            display: none;
        }
    }
`;

export const EditSelect = styled.select`
    width: 100%;
    padding: 0.5rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: white;
    font-size: 0.875rem;

    &:focus {
        outline: none;
        border-color: var(--primary-color);
        box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
    }
`;

export const EditInput = styled.input<{ gradientBackground?: boolean }>`
    width: 100%;
    padding: 0.5rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.875rem;
    ${(props) => props.gradientBackground && 'background: #f8f9fa;'}

    &:focus {
        outline: none;
        border-color: var(--primary-color);
        box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
    }
`;

export const HideOverflow = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const TypeBadge = styled.span<{ variant: 'created' | 'updated' | 'deleted' }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
    white-space: nowrap;

    ${(props) => {
        switch (props.variant) {
            case 'created':
                return `
                    background: rgb(224, 233, 204);
                    color: rgb(48, 80, 5);
                `;
            case 'updated':
                return `
                    background: rgb(218, 233, 248);
                    color: rgb(0, 50, 100);
                `;
            case 'deleted':
                return `
                    background: rgb(248, 215, 218);
                    color: rgb(120, 40, 40);
                `;
            default:
                return `
                    background: #eee;
                    color: #666;
                `;
        }
    }}
`;

export const StatusBadge = styled.span<{ status: 'reviewed' | 'pending' }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
    white-space: nowrap;

    ${(props) => {
        switch (props.status) {
            case 'reviewed':
                return `
                    background: rgb(224, 233, 204);
                    color: rgb(48, 80, 5);
                `;
            case 'pending':
                return `
                    background: rgb(255, 240, 210);
                    color: rgb(120, 80, 0);
                `;
            default:
                return `
                    background: #eee;
                    color: #666;
                `;
        }
    }}
`;

export const Loading = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
    font-size: 1.1rem;
    color: var(--text-secondary);
`;

export const Error = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
    font-size: 1.1rem;
    color: #dc3545;
    background: #f8d7da;
    border: 1px solid #f5c6cb;
    border-radius: 4px;
    margin: 1rem 0;
`;

export const NoData = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
    font-size: 1.1rem;
    color: var(--text-secondary);
    font-style: italic;
`;

export const UserCell = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

export const UserAvatar = styled.div`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgb(0, 68, 126);
    color: rgb(255, 255, 255);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 700;
    flex-shrink: 0;
    letter-spacing: 0.3px;
`;